import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout'

const BREADCRUMB = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://myschoolportal.xyz' },
    { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: 'https://myschoolportal.xyz/privacy' },
  ],
}

const SECTIONS = [
  {
    id: 'introduction',
    heading: 'Introduction and scope',
    body: (
      <>
        <p>
          This Privacy Policy explains how Tvantra (&ldquo;<strong>Tvantra</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;,
          &ldquo;<strong>us</strong>&rdquo;) collects, uses, discloses and protects personal data in connection with the
          MySchoolPortal software platform (the &ldquo;<strong>Platform</strong>&rdquo;).
        </p>
        <p>
          It applies to personal data processed through the Platform. It does not apply to the independent
          data-handling practices of any school, or of any third party whose services a school chooses to use.
        </p>
      </>
    ),
  },
  {
    id: 'roles',
    heading: 'Controller and processor',
    body: (
      <>
        <p>
          Under the Data Protection Act, 2012 (Act 843) of Ghana, a subscribing school (the
          &ldquo;<strong>School</strong>&rdquo;) is the <strong>data controller</strong> for the records it keeps in the
          Platform. The School determines what personal data is entered, for what purpose, and for how long it is
          retained.
        </p>
        <p>
          Tvantra acts as a <strong>data processor</strong>, processing that data on the School&rsquo;s documented
          instructions and for no independent purpose of our own. Where we determine the means of processing (for
          example in relation to account security, billing and service administration), we act as a controller in
          respect of that limited processing.
        </p>
        <p>
          If you are a parent, guardian, pupil or member of staff, your first point of contact in relation to your
          personal data is the School, not Tvantra. Section 9 explains how we support such requests.
        </p>
      </>
    ),
  },
  {
    id: 'categories',
    heading: 'Categories of personal data',
    body: (
      <>
        <p>The Platform processes the following categories of personal data:</p>
        <ul>
          <li><strong>Pupil data</strong>: name, class, date of birth, admission details, photograph where uploaded by the School, and guardian contact details.</li>
          <li><strong>Financial data</strong>: fees charged, payments received, outstanding balances, credit balances, and receipts issued.</li>
          <li><strong>Attendance and academic data</strong>: daily registers, assessment scores, grades and report cards.</li>
          <li><strong>Staff account data</strong>: name, role, contact details, authentication credentials and assigned permissions.</li>
          <li><strong>Communications data</strong>: recipient numbers and message content for SMS sent by the School, together with delivery status.</li>
          <li><strong>Audit data</strong>: a log attributing each material change to the account that made it, together with a timestamp.</li>
        </ul>
        <p>
          Some of this constitutes personal data relating to children. It is processed solely to deliver the Platform
          to the School, and is never used for marketing or profiling.
        </p>
      </>
    ),
  },
  {
    id: 'purposes',
    heading: 'Purposes and lawful basis',
    body: (
      <>
        <p>We process personal data for the following purposes:</p>
        <ul>
          <li><strong>Provision of the Platform</strong>: maintaining records, generating statements, invoices and report cards, and transmitting SMS at the School&rsquo;s direction. Lawful basis: performance of our contract with the School.</li>
          <li><strong>Security and audit</strong>: authenticating users, enforcing permissions, and maintaining audit logs. Lawful basis: legitimate interests in securing the Platform, and the School&rsquo;s legal obligation to maintain accurate records.</li>
          <li><strong>Support and fault diagnosis</strong>: investigating issues reported by the School. Lawful basis: performance of our contract with the School.</li>
          <li><strong>Billing and administration</strong>: invoicing subscription and SMS charges. Lawful basis: performance of our contract, and compliance with legal obligations.</li>
        </ul>
        <p>
          We do not sell personal data, licence it to third parties, or use it for advertising or automated
          decision-making that produces legal effects.
        </p>
      </>
    ),
  },
  {
    id: 'subprocessors',
    heading: 'Sub-processors and disclosures',
    body: (
      <>
        <p>
          We engage the following sub-processors, each bound by contract to appropriate confidentiality and security
          obligations, and each receiving only the data necessary for its function:
        </p>
        <ul>
          <li><strong>mNotify and Hubtel</strong> (SMS delivery): recipient telephone number and message content.</li>
          <li><strong>Paystack</strong> (payment processing): transaction data where a School collects fees online. Cardholder data is processed by Paystack under its own PCI-DSS obligations and is not stored by Tvantra.</li>
          <li><strong>Amazon Web Services</strong> (infrastructure): hosting of the application, database and uploaded files.</li>
        </ul>
        <p>
          We may also disclose personal data where required by law, court order or a lawful request from a competent
          authority, or where necessary to establish, exercise or defend legal claims. We will notify the School of
          any such disclosure unless legally prohibited from doing so.
        </p>
      </>
    ),
  },
  {
    id: 'access',
    heading: 'Access controls',
    body: (
      <>
        <p>
          Each School&rsquo;s data is logically separated from that of every other School. Within a School, access is
          governed by role-based permissions assigned by that School&rsquo;s administrators; a class teacher&rsquo;s
          access is narrower than an administrator&rsquo;s.
        </p>
        <p>
          Tvantra personnel access School data only where necessary to provide support, investigate a fault, or comply
          with a legal obligation. Such access is limited to authorised personnel and is recorded.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    heading: 'Retention and deletion',
    body: (
      <>
        <p>
          We retain personal data for as long as the School maintains an active subscription, as the records
          constitute the School&rsquo;s operational and financial history.
        </p>
        <p>
          Certain financial records are retained notwithstanding a deletion request. A cancelled or voided payment is
          marked as void and preserved, together with the reason and the account that voided it, so that the financial
          audit trail remains complete. This is a deliberate control and may also be required for the School&rsquo;s own
          statutory record-keeping.
        </p>
        <p>
          On termination, we will agree a data export and deletion schedule with the School. Following that schedule,
          data is deleted from active systems, with residual copies in routine backups expiring in the ordinary course.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    heading: 'Security measures',
    body: (
      <>
        <p>
          We maintain technical and organisational measures appropriate to the risk, including individual
          authentication credentials, role-based access control, encryption of data in transit, and audit logging that
          attributes changes to the acting account.
        </p>
        <p>
          No system can be guaranteed secure. Where a personal data breach occurs that is likely to affect a
          School&rsquo;s data, we will notify the affected School without undue delay and provide the information
          reasonably required for it to meet its own obligations under Act 843.
        </p>
      </>
    ),
  },
  {
    id: 'rights',
    heading: 'Data subject rights',
    body: (
      <>
        <p>
          Subject to Act 843, data subjects have the right to request access to their personal data, to request
          correction of inaccurate data, to object to processing in defined circumstances, and to request erasure
          where no lawful ground for continued retention applies.
        </p>
        <p>
          Because the School is the controller, such requests should be addressed to the School in the first
          instance. Where a School requires our assistance to respond, we will provide it within the timeframes
          required by law. Where we receive a request directly, we will refer it to the relevant School and inform the
          requester that we have done so.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to this Policy',
    body: (
      <p>
        We may amend this Policy from time to time. Where an amendment materially affects the rights of data subjects
        or the obligations of Schools, we will notify Schools directly rather than relying on publication alone.
      </p>
    ),
  },
  {
    id: 'contact',
    heading: 'Contact',
    body: (
      <>
        <p>Enquiries and requests relating to this Policy may be directed to:</p>
        <p>
          <strong>Tvantra</strong>: operator of MySchoolPortal<br />
          Email: <a href="mailto:info@myschoolportal.xyz">info@myschoolportal.xyz</a><br />
          Telephone: <a href="tel:+233596539060">+233 59 653 9060</a><br />
          Or via the <Link to="/contact">contact page</Link>.
        </p>
        <p>
          Data subjects in Ghana additionally have the right to lodge a complaint with the Data Protection Commission.
        </p>
      </>
    ),
  },
]

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      intro="How Tvantra collects, uses and protects personal data processed through the MySchoolPortal platform, and the rights available to data subjects under the Data Protection Act, 2012 (Act 843)."
      path="/privacy"
      schema={[BREADCRUMB]}
      sections={SECTIONS}
    />
  )
}
